import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { SourceDocument } from "../../../../api/content/sourcesApi";
import { formatDocumentVersionRef } from "../../../../shared/references/contentReferences";
import type { EvidenceMark } from "../../../annotation/textEvidence";
import type { ChatReference } from "../../../chatReferences";
import { absorbNamedIndividuals, describeStatement, groupIndividuals, layOutStatement, mostSpecificClasses, nestSaid, saidFirst, type ShownStatement } from "../../../knowledgeData/abox/aboxViewModel";
import { useAboxViewProfile } from "../../../knowledgeData/abox/aboxViewProfile";
import { statementCard } from "../../../knowledgeData/abox/statementCard";
import { DetailLevelContext, useDetailLevel } from "../../../knowledgeData";
import { termName, useTermCardsByCommit } from "../../../ontology";
import DocumentParts, { type DrawnAnnotations } from "../parts/DocumentParts";
import { conclusionsOf } from "./conclusions/regestConclusions";
import PartStatements from "./PartStatements";
import { partFacts, regestSummary, type TextFacts } from "./regestFacts";
import RegestHeader, { TextHead } from "./RegestHeader";
import RegestSections from "./RegestSections";
import { itemKeys, regestParts, type PlacedStatement, type RegestResult } from "./regestParts";

interface TextWithStatementsProps {
  readonly document: Pick<SourceDocument, "ref" | "fullText" | "segments" | "metadata">;
  /**
   * The project the statements belong to, whose view profile lays them out and whose model
   * versions name their terms (AB-5), whatever the project menu chooses meanwhile (Sol review R3);
   * `null` when it isn't known: then every row shows, its terms by their own names.
   */
  readonly project: string | null;
  /** The statements drawn, accepted or not (F154); evidence on other documents or versions is not drawn. */
  readonly results: readonly RegestResult[];
  /** The annotation set drawn on the text; `null` draws none. */
  readonly annotations: DrawnAnnotations | null;
  /** Where selecting text asks the agent about it (F96); `undefined` without a project. */
  readonly askInProject: string | undefined;
  /** What a selection in the text also sends, such as the annotation set on screen (PC-6). */
  readonly askWith: Omit<ChatReference, "quote"> | null;
  /** What a statement's line also shows, such as the parts of a vita it came from. */
  readonly statementExtra?: (statement: PlacedStatement) => ReactNode;
}

/**
 * A document's text with the statements about it (O-1), for every page that shows instance data:
 * the Documents page, a result's page and its review steps (F154: one view, before and after
 * acceptance). The text shows once, part by part; each statement sits beside the part that holds
 * most of its words, once (`absorbNamedIndividuals`); one with no words in this text follows it.
 * Pointing at a statement or its words highlights both; clicking keeps them highlighted and
 * brings the other side into view; Escape lets go.
 */
export default function TextWithStatements({ document, project, results, annotations, askInProject, askWith, statementExtra }: TextWithStatementsProps) {
  const { t, i18n } = useTranslation();
  const [pointed, setPointed] = useState<readonly string[] | null>(null);
  const [selected, setSelected] = useState<readonly string[] | null>(null);
  const container = useRef<HTMLDivElement>(null);
  const shown = formatDocumentVersionRef(document.ref);
  const language = i18n.resolvedLanguage ?? "en";
  const individuals = useMemo(() => groupIndividuals(results.flatMap((result) => result.statements), language), [results, language]);
  const profile = useAboxViewProfile(project);
  const placement = useMemo(() => regestParts(shown, document.segments, results), [shown, document.segments, results]);
  const commits = useMemo(() => [...new Set([...placement.parts.values(), placement.unplaced].flat().map((placed) => placed.modelCommit))].filter((commit) => commit !== ""), [placement]);
  const cards = useTermCardsByCommit(project, commits);
  const { byPart, unplaced } = useMemo(() => {
    // Each statement is laid out once, by its project's profile and its model version's terms
    // (AB-5); until both are read, the text shows without them, so nothing jumps when they come.
    if (profile === null || commits.some((commit) => !cards.has(commit))) return { byPart: new Map<string, ShownStatement<PlacedStatement>[]>(), unplaced: [] };
    const layOut = (placed: PlacedStatement) => {
      const terms = cards.get(placed.modelCommit) ?? null;
      const descriptions = describeStatement(placed.statement, individuals, (iri) => terms?.has(iri) === true);
      return layOutStatement(descriptions, profile, (iri) => terms?.get(iri)?.parents ?? []);
    };
    return {
      byPart: new Map([...placement.parts].map(([segmentPath, statements]) => [segmentPath, saidFirst(absorbNamedIndividuals(statements, layOut))])),
      unplaced: saidFirst(absorbNamedIndividuals(placement.unplaced, layOut)),
    };
  }, [placement, commits, individuals, cards, profile]);
  // Every statement key leads to all the keys of the line that shows it.
  const allLines = useMemo(() => [...byPart.values(), unplaced].flat(), [byPart, unplaced]);
  const lineKeys = useMemo(() => new Map(allLines.flatMap((item) => itemKeys(item).map((key) => [key, itemKeys(item)] as const))), [allLines]);
  // Conclusions (Daniel's "erschlossen") leave the parts for their own section after the texts, by the entities
  // coder's one rule (`conclusionsOf`), numbered after the text's statements as in his viewer.
  const conclusions = useMemo(() => (profile === null ? [] : conclusionsOf(allLines, profile.conclusions)), [allLines, profile]);
  const { partLines, unplacedLines, sectionLines } = useMemo(() => {
    const concluded = new Set(conclusions.map((conclusion) => conclusion.line));
    const kept = (items: readonly ShownStatement<PlacedStatement>[]) => items.filter((item) => !concluded.has(item));
    const parts = new Map([...byPart].map(([segmentPath, items]) => [segmentPath, kept(items)] as const));
    const rest = kept(unplaced);
    return { partLines: parts, unplacedLines: rest, sectionLines: [...parts.values(), rest].flat() };
  }, [byPart, unplaced, conclusions]);
  // Statements are numbered in reading order, as in Daniel's viewer. Where the profile shapes a card, only a line
  // that reads as one is a statement; one that only names an entity is not numbered (usability round 3).
  const lines = useMemo(() => [...sectionLines, ...conclusions.map((conclusion) => conclusion.line)].filter((item) => item.layout.said.length > 0
    && (profile?.statements == null || statementCard(nestSaid(item.layout.said)[0], profile.statements) !== null)), [sectionLines, conclusions, profile]);
  const numbers = useMemo(() => new Map(lines.map((item, index) => [item.shown.key, index + 1])), [lines]);
  // "#n kind" by statement key: the bars on the text and the references in the sections after it read the same label.
  // Each bar's colour follows its kind's class, the same in every language (usability round 4).
  // A value in one statement that names what another is about reads as that one's label and chooses its line.
  const { statementLabels, statementTones, aboutLabels, aboutKeys } = useMemo(() => {
    const labelOf = new Map<string, string>();
    const toneOf = new Map<string, string>();
    const labelOfAbout = new Map<string, string>();
    const keysOfAbout = new Map<string, readonly string[]>();
    lines.forEach((item, index) => {
      const terms = cards.get(item.shown.modelCommit) ?? null;
      const about = statementCard(nestSaid(item.layout.said)[0], profile?.statements ?? null)?.about.individual;
      const classes = about === undefined ? [] : mostSpecificClasses(about.classes, (iri) => terms?.get(iri)?.parents ?? []);
      const label = [`#${String(index + 1)}`, ...classes.map((iri) => termName(iri, terms, language))].join(" ");
      for (const key of itemKeys(item)) {
        if (!labelOf.has(key)) labelOf.set(key, label);
        if (!toneOf.has(key) && classes[0] !== undefined) toneOf.set(key, classes[0]);
      }
      if (about !== undefined && !labelOfAbout.has(about.iri)) {
        labelOfAbout.set(about.iri, label);
        keysOfAbout.set(about.iri, itemKeys(item));
      }
    });
    return { statementLabels: labelOf, statementTones: toneOf, aboutLabels: labelOfAbout, aboutKeys: keysOfAbout };
  }, [lines, cards, profile, language]);
  // The part each statement's line sits beside; a conclusion or an unplaced line sits in none.
  const partOf = useMemo(() => new Map([...partLines].flatMap(([segmentPath, items]) => items.flatMap((item) => itemKeys(item).map((key) => [key, segmentPath] as const)))), [partLines]);
  const marks = useMemo((): EvidenceMark[] => {
    const drawn = results.flatMap((result) => result.statements.flatMap((statement) => statement.evidence
      .filter((span) => span.segment.split("#")[0] === shown)
      .map((span) => ({ key: `${result.source}#${String(statement.number)}`, start: span.start, end: span.end, segmentPath: displayedPart(document.segments, span.start) }))));
    // Each span of a numbered statement is drawn as the annotations' labelled bar, "#n kind" (Johannes, 4 Nov 06:51:
    // "the annotation view is a must with the spans, similar to the regest viewer"; 10:46: "I don'T see the spans"), in
    // the part its line sits beside; its words in other parts keep the plain highlight, so a name that several texts'
    // statements cite doesn't stack their bars (usability round 4).
    return drawn.map(({ segmentPath, ...mark }) => {
      const label = statementLabels.get(mark.key);
      const part = partOf.get(mark.key);
      if (label === undefined || (part !== undefined && part !== segmentPath)) return mark;
      const tone = statementTones.get(mark.key);
      return { ...mark, label, ...(tone === undefined ? {} : { tone }) };
    });
  }, [results, shown, document.segments, statementLabels, statementTones, partOf]);

  const linesOf = (keys: readonly string[]) => [...new Set(keys.flatMap((key) => lineKeys.get(key) ?? [key]))];
  const focus = {
    highlighted: new Set(pointed ?? selected ?? []),
    selected: new Set(selected ?? []),
    onPoint: (keys: readonly string[] | null) => { setPointed(keys === null ? null : linesOf(keys)); },
    onSelect: (keys: readonly string[]) => {
      const lines = linesOf(keys);
      const again = selected !== null && lines.every((key) => selected.includes(key));
      setSelected(again ? null : lines);
      if (again) return;
      // The other side comes into view: the line for clicked words, the words for a clicked line.
      requestAnimationFrame(() => {
        container.current?.querySelector(`[data-statements~=${JSON.stringify(lines[0] ?? "")}]`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
        container.current?.querySelector(".app-evidence-highlighted")?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      });
    },
  };
  const [level, setLevel] = useDetailLevel();
  const readings = useMemo(() => ({ dates: profile?.dates ?? null, amounts: profile?.amounts ?? null }), [profile]);
  // The regest's header and each text's head (Daniel's render_vita and render_source), from the profile's `texts`.
  const nameOf = useCallback((iri: string) => {
    const terms = [...cards.values()].find((each) => each?.has(iri) === true) ?? null;
    return termName(iri, terms, language);
  }, [cards, language]);
  const facts = useMemo(() => (profile === null ? new Map<string, TextFacts>() : partFacts(byPart, profile, (iri) => [...cards.values()].find((each) => each?.has(iri) === true)?.get(iri)?.parents ?? [], language)), [byPart, profile, cards, language]);
  const summary = useMemo(() => regestSummary(Math.max(document.segments.length, 1), lines, facts), [document.segments.length, lines, facts]);
  const heads = useMemo(() => new Map([...facts].map(([segmentPath, fact]) => [segmentPath, <TextHead key={segmentPath} facts={fact} nameOf={nameOf} />] as const)), [facts, nameOf]);
  const references = { labelOf: aboutLabels, onChoose: (about: string) => { focus.onSelect(aboutKeys.get(about) ?? []); } };
  const statementsOf = (key: string, items: typeof unplaced) => <PartStatements key={key} items={items} cards={cards} focus={focus} askInProject={askInProject} extra={statementExtra} numbers={numbers} shape={profile?.statements ?? null} readings={readings} references={references} />;
  return (
    <div ref={container} onKeyDown={(event) => { if (event.key === "Escape") setSelected(null); }}>
      <DetailLevelContext.Provider value={level}>
      {profile === null ? null : <RegestHeader summary={lines.length === 0 ? null : summary} level={level} onLevel={setLevel} />}
      <DocumentParts
        heads={heads}
        document={document}
        annotations={annotations}
        evidence={marks.length === 0 ? null : { marks, ...focus }}
        asides={new Map([...partLines].map(([segmentPath, items]) => [segmentPath, statementsOf(segmentPath, items)]))}
        askInProject={askInProject}
        askWith={askWith}
      />
      {unplacedLines.length === 0 ? null : (
        <section className="space-y-1 py-3">
          <h4 className="app-label text-xs">{t("sources.document.regest.unplaced")}</h4>
          {statementsOf("unplaced", unplacedLines)}
        </section>
      )}
      {profile === null ? null : (
        <RegestSections
          document={shown}
          lines={sectionLines}
          conclusions={conclusions}
          labels={statementLabels}
          numbers={numbers}
          focus={focus}
          profile={profile}
          cards={cards}
          individuals={individuals}
          placement={placement}
          askInProject={askInProject}
          references={references}
          extra={statementExtra}
          readings={readings}
        />
      )}
      </DetailLevelContext.Provider>
    </div>
  );
}

/**
 * The displayed part a span starts in, by its offsets, as `regestParts` places statements: evidence may name an
 * enclosing segment (`#entry.1`) while the text shows its leaf parts (Sol R12). `""` for a text without parts.
 */
function displayedPart(segments: readonly { readonly segmentPath: string; readonly start: number; readonly end: number }[], start: number): string {
  return segments.find((segment) => segment.start <= start && start < segment.end)?.segmentPath ?? "";
}
